import React, { useEffect, useState } from "react";
import ReportsMenuTab from "./ReportsMenuTab";
import { PageTitle } from "src/shared/PageTitle";
import CommonTable from "src/business-types/grocery/components/CommonTable";
import api from "src/features/auth/axiosConfig";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface ReportTableRow {
  id: string | number;
  sno: number;
  fileName: string;
  createdAt: string;
  actions?: React.ReactNode;
}

export interface SummaryReport {
  _id: string;
  orgId: string;
  reportType: string;
  reportDate: string;
  fileName: string;
  fileKey: string;
  fileUrl: string;
  createdAt: string;
  createdBy: string;
}

interface HeadCell<T> {
  id: keyof T;
  label: string;
  align?: "left" | "center" | "right";
  sorting?: boolean;
  numeric?: boolean;
  disablePadding?: boolean;
}

const BusinessOperation: React.FC = () => {
  const [salesSummary, setSalesSummary] = useState<SummaryReport[]>([]);

  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<{ id: string; fileName: string } | null>(null);

  useEffect(() => {
    document.title = "Sales Summary › Business Operation";
    fetchSalesSummary();
  }, []);

  const fetchSalesSummary = async () => {
    try {
      const response = await api.get("/app/pos/v2/get_reports?report_type=DayCloseReport");

      const reports: SummaryReport[] = response?.data?.results || [];

      setSalesSummary(reports);
    } catch (error) {
      console.error("Error fetching sales summary:", error);
      toast.error("Failed to load reports", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (reportId: string, fileName: string) => {
    setReportToDelete({ id: reportId, fileName });
    setIsDeleteModalOpen(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setReportToDelete(null);
  };

  // Handle actual delete after confirmation
  const handleConfirmDelete = async () => {
    if (!reportToDelete?.id) {
      toast.error("Invalid report ID", {
        position: "top-right",
        autoClose: 3000,
      });
      closeDeleteModal();
      return;
    }

    try {
      await api.delete(`/app/pos/v2/report/${reportToDelete.id}`);

      setSalesSummary((prev) => prev.filter((item) => item._id !== reportToDelete.id));

      toast.success("Report deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
      });

      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Failed to delete report", {
        position: "top-right",
        autoClose: 3000,
      });
      closeDeleteModal();
    }
  };

  const handleViewReport = async (fileUrl: string) => {
    if (!fileUrl) {
      toast.error("Report not found", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Open a new tab immediately to prevent browser popup blockers
    const newTab = window.open("", "_blank");
    if (!newTab) {
      toast.error("Popup blocked! Please allow popups for this site.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Show a modern, styled loading indicator in the new tab
    newTab.document.write(`
      <html>
        <head>
          <title>Loading Report PDF...</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
              color: #333333;
            }
            .loader-container {
              background: rgba(255, 255, 255, 0.85);
              padding: 30px 50px;
              border-radius: 16px;
              box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
              backdrop-filter: blur(10px);
              border: 1px solid rgba(255, 255, 255, 0.2);
              text-align: center;
              max-width: 400px;
              width: 80%;
            }
            .spinner {
              border: 4px solid rgba(0, 0, 0, 0.05);
              border-top: 4px solid #3b82f6;
              border-radius: 50%;
              width: 45px;
              height: 45px;
              animation: spin 1s linear infinite;
              margin: 0 auto 20px;
            }
            .title {
              font-size: 1.1rem;
              font-weight: 600;
              margin: 0 0 8px 0;
              color: #1f2937;
            }
            .subtitle {
              font-size: 0.875rem;
              color: #6b7280;
              margin: 0;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div class="loader-container">
            <div class="spinner"></div>
            <div class="title">Preparing Report</div>
            <div class="subtitle">Fetching and rendering PDF document...</div>
          </div>
        </body>
      </html>
    `);
    newTab.document.close();

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const blob = await response.blob();
      const pdfBlob = new Blob([blob], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(pdfBlob);
      newTab.location.replace(blobUrl);
    } catch (err) {
      console.error("Error viewing PDF:", err);
      newTab.close();
      toast.error("Failed to load PDF inline. Attempting standard download...", {
        position: "top-right",
        autoClose: 3000,
      });
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const tableData: ReportTableRow[] = salesSummary.map((report, index) => ({
    id: report._id,
    sno: index + 1,
    fileName: report.fileName,
    createdAt: new Date(report.createdAt).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    actions: (
      <div className="flex items-center justify-center gap-2">
        <button
          className="h-8 px-3 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          onClick={() => handleViewReport(report.fileUrl)}
        >
          View
        </button>
        <button
          className="h-8 px-3 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          onClick={() => openDeleteModal(report._id, report.fileName)}
        >
          Delete
        </button>
      </div>
    ),
  }));

  const headCells: HeadCell<ReportTableRow>[] = [
    { id: "sno", label: "S.No", sorting: false, align: "left" },
    { id: "fileName", label: "File Name", align: "center" },
    { id: "createdAt", label: "Close On", align: "center", sorting: true },
    { id: "actions", label: "Actions", align: "center", sorting: false },
  ];

  return (
    <div className="p-4 w-full">
      <PageTitle title="- Sales Summary" />
      <ReportsMenuTab />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 mt-4">
        <h2 className="text-xl font-semibold ml-9">Summary</h2>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && reportToDelete && (
        <ModalWrapper title="Confirm Delete" onClose={closeDeleteModal} showBackButton={false} width="max-w-md" positionClass="top-0 left-0 right-0 mt-10 h-full">
          <div className="text-center">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Are you sure you want to delete this report?</h3>
              <p className="text-sm text-gray-500 mb-4">
                Report: <span className="font-medium text-gray-700">{reportToDelete.fileName}</span>
              </p>
            </div>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Delete
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}

      <div className="mt-3">
        <CommonTable<ReportTableRow> data={tableData} headCells={headCells} title="Generated Reports" selectable={false} showToolbar={false} pagenation={true} pageSize={10} denseDefault={false} />
      </div>

      {/* Toast Container for notifications */}
      <ToastContainer position="top-right" autoClose={2000} />
    </div>
  );
};

export default BusinessOperation;
